import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import {
  Package, ShoppingCart, Users, TrendingUp,
  MessageCircle, FileText, DollarSign, UserCheck
} from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { authenticated, currentUser, loading, getAuthHeader } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !authenticated) navigate('/admin/login');
  }, [authenticated, loading, navigate]);

  useEffect(() => {
    if (authenticated) fetchStats();
  }, [authenticated]);

  const fetchStats = async () => {
    try {
      const headers = getAuthHeader();
      const [products, orders, investors, investments] = await Promise.all([
        axios.get(`${API}/admin/products`, { headers }).catch(() => ({ data: { products: [] } })),
        axios.get(`${API}/admin/orders`, { headers }).catch(() => ({ data: { orders: [] } })),
        axios.get(`${API}/admin/investors`, { headers }).catch(() => ({ data: { investors: [], stats: {} } })),
        axios.get(`${API}/admin/investments`, { headers }).catch(() => ({ data: { investments: [], stats: {} } })),
      ]);
      setStats({
        products: products.data.products?.length || 0,
        orders: orders.data.orders?.length || 0,
        investors: investors.data.stats?.total_investors || 0,
        investments: investments.data.stats?.total_count || 0,
        totalInvested: investments.data.stats?.total_invested || 0,
      });
    } catch { /* stats are optional */ }
  };

  if (loading || !authenticated) return null;

  const statCards = [
    { label: 'Total Orders', value: stats?.orders ?? '—', icon: ShoppingCart, color: 'bg-emerald-500', link: '/admin/orders' },
    { label: 'Products', value: stats?.products ?? '—', icon: Package, color: 'bg-blue-500', link: '/admin/products' },
    { label: 'Investors', value: stats?.investors ?? '—', icon: UserCheck, color: 'bg-purple-500', link: '/admin/investments' },
    { label: 'Investments', value: stats?.investments ?? '—', icon: TrendingUp, color: 'bg-amber-500', link: '/admin/investments' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome, {currentUser?.full_name || currentUser?.username || 'Admin'}
        </h1>
        <p className="text-sm text-gray-500">Here's an overview of your farm's operations</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((s) => {
          const Icon = s.icon;
          return (
            <Link key={s.label} to={s.link}>
              <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`${s.color} p-3 rounded-lg`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{s.label}</p>
                    <p className="text-2xl font-bold">{s.value}</p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/admin/chat">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-pink-500 p-3 rounded-lg">
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold">Customer Chat</p>
                <p className="text-xs text-gray-500">Respond to customer inquiries</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/blog">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-indigo-500 p-3 rounded-lg">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold">Blog Posts</p>
                <p className="text-xs text-gray-500">Create and manage articles</p>
              </div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/orders">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="bg-green-500 p-3 rounded-lg">
                <DollarSign className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="font-semibold">Revenue</p>
                <p className="text-xs text-gray-500">₦{(stats?.totalInvested || 0).toLocaleString()} invested</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Session Info */}
      <div className="mt-8 p-3 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-700">
          Session auto-expires after 10 minutes of inactivity. You'll receive a warning one minute before logout.
        </p>
      </div>
    </div>
  );
};

export default AdminDashboard;
